import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Trophy, Sparkles } from "lucide-react";
import { api } from "../lib/api";

const TIER_BG = {
  ultra:       "from-fuchsia-400/30 to-fuchsia-200/10 border-fuchsia-400/40",
  mythic:      "from-violet-400/30 to-violet-200/10 border-violet-400/40",
  diamond:     "from-cyan-400/30 to-cyan-200/10 border-cyan-400/40",
  platinum:    "from-zinc-200/30 to-zinc-100/10 border-zinc-200/40",
  gold:        "from-amber-400/30 to-amber-200/10 border-amber-400/40",
  silver_plus: "from-slate-200/30 to-slate-100/10 border-slate-200/30",
  silver:      "from-slate-400/20 to-slate-300/10 border-slate-400/30",
  bronze_plus: "from-amber-600/20 to-amber-800/5 border-amber-600/30",
  bronze:      "from-amber-700/20 to-amber-900/5 border-amber-700/30",
  newbie:      "from-zinc-700/15 to-zinc-800/5 border-zinc-700/30",
};

const RankMedal = ({ rank }) => {
  if (rank === 1) return <span className="text-2xl">🥇</span>;
  if (rank === 2) return <span className="text-2xl">🥈</span>;
  if (rank === 3) return <span className="text-2xl">🥉</span>;
  return <span className="pixel-text text-lg text-zinc-500">#{rank}</span>;
};

const Leaderboard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const d = await api.leaderboard(100);
        setRows(d || []);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <section className="mx-auto max-w-4xl px-4 py-10" data-testid="leaderboard-page">
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#ffb84d]">Punkte-Ranking</div>
      <h1 className="mt-1 flex items-center gap-2 text-3xl font-bold text-white">
        <Trophy className="h-7 w-7 text-amber-300" /> Habthek Leaderboard
      </h1>
      <p className="mt-2 max-w-2xl text-sm text-zinc-400">
        Sammle Punkte durch Kommentare, Stories, Verifizierung und tägliche Besuche – schalte exklusive Tier-Boni frei.
      </p>

      <div className="mt-6 rounded-2xl border border-white/10 bg-[#0f141b] p-4">
        <div className="mb-3 grid grid-cols-2 gap-2 text-center text-[10px] uppercase tracking-wider text-zinc-500 md:grid-cols-5">
          <div>🐣 Newbie (0+)</div>
          <div>🥉 Bronze (10+)</div>
          <div>⭐ Bronze+ (50+)</div>
          <div>🥈 Silber (100+)<div className="text-[9px] normal-case text-zinc-600">Nick-Farbe</div></div>
          <div>✨ Silber+ (250+)</div>
          <div>🥇 Gold (500+)<div className="text-[9px] normal-case text-zinc-600">Custom Glow</div></div>
          <div>🪩 Platin (1.2k+)<div className="text-[9px] normal-case text-zinc-600">VIP-Krone</div></div>
          <div>💎 Diamant (2k+)<div className="text-[9px] normal-case text-zinc-600">Rainbow-Nick</div></div>
          <div>🌟 Mythisch (5k+)</div>
          <div>🦄 Ultra Legend (10k+)<div className="text-[9px] normal-case text-zinc-600">Animierte Glows</div></div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-zinc-500">Noch keine Punkte vergeben.</div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <Link
                key={r.habbo_name}
                to={`/u/${encodeURIComponent(r.habbo_name)}`}
                data-testid={`leaderboard-row-${r.habbo_name}`}
                className={`flex items-center gap-3 rounded-xl border bg-gradient-to-r p-3 hover:scale-[1.01] transition-transform ${TIER_BG[r.tier?.id] || TIER_BG.newbie}`}
              >
                <div className="w-12 flex-shrink-0 text-center">
                  <RankMedal rank={r.rank} />
                </div>
                <img src={r.avatar} alt={r.habbo_name} className="hb-pixel-img h-12 w-12 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-base font-bold text-white" style={r.name_color ? { color: r.name_color } : undefined}>
                      {r.habbo_name}
                    </span>
                    {r.verified && <Sparkles className="h-3 w-3 text-emerald-300" />}
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" style={{ color: r.tier?.color, backgroundColor: `${r.tier?.color}25` }}>
                      {r.tier?.icon} {r.tier?.label}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="pixel-text text-2xl text-white">{r.points.toLocaleString("de-DE")}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Punkte</div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Leaderboard;
